import { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { ColumnConfig } from '@/__configs/config_types';
import Cell from './Cell';
import { darken } from '@mui/material';
import { TableData } from './PolarisTableContainer';
import PolarisIcon from '../../PolarisIcon';
import EllipsisTooltip from '../EllipsisTooltip';
import { ScrollPosition } from './PolarisTableContainer.types';
import { ROW_HEIGHT } from '@/components/_common/Table/PolarisTableContainer/constants';

interface TableRowProps {
  row: any;
  position: keyof TableData['tables'];
  gridTemplateColumns: string;
  headers: ColumnConfig<any, any>[];
  onToggle?: (val: number) => void;
  index: number;
  currentDrawerHeight?: number;
  animationTime: string;
  scrollPosition: ScrollPosition;
  expanded: boolean;
  scrollableCellMinWidth: number;
  onRowClick?: (index: number) => void;
  frozenRightWidth?: number;
}

const TableRow = ({
  row,
  scrollPosition,
  gridTemplateColumns,
  animationTime,
  currentDrawerHeight = 0,
  headers,
  position,
  onToggle,
  index,
  expanded,
  scrollableCellMinWidth,
  onRowClick,
  frozenRightWidth
}: TableRowProps) => {
  const [localDrawerHeight, setLocalDrawerHeight] = useState<number>(0);
  const [localExpanded, setLocalExpanded] = useState<boolean>(expanded);
  const ellipsisTooltipRef = useRef<HTMLDivElement>(null);
  const [maxWidth, setMaxWidth] = useState<number | undefined>(undefined);

  const renderCellValue = (header: ColumnConfig<any, any>) => {
    if (header?.id === 'expandable') {
      return (
        <IconWrapper expanded={+localExpanded}>
          <PolarisIcon icon="right" height={20} width={20} />
        </IconWrapper>
      );
    } else if (header?.render && header?.id) {
      const address = header?.id.split('__');
      return header.render(address.reduce((acc, part) => acc?.[part], row), row, expanded, {
        width: frozenRightWidth,
        height: localDrawerHeight + ROW_HEIGHT
      });
    }

    if (header?.id && header.id.includes('__')) {
      const address = header.id.split('__');
      return address.reduce((acc, part) => acc?.[part], row);
    } else {
      const value = row[header.id as keyof typeof headers];
      if (typeof value === 'string') {
        return (
          <div ref={ellipsisTooltipRef} style={{ width: '100%' }}>
            {ellipsisTooltipRef.current && maxWidth && <EllipsisTooltip text={value} maxWidth={maxWidth} />}
          </div>
        );
      } else {
        return value;
      }
    }
  };

  useEffect(() => {
    setLocalDrawerHeight(currentDrawerHeight);
  }, [currentDrawerHeight]);

  useEffect(() => {
    setLocalExpanded(expanded);
  }, [expanded]);

  useEffect(() => {
    const handleResize = () => {
      if (ellipsisTooltipRef.current) {
        const availableWidth = ellipsisTooltipRef.current.clientWidth - 20;
        setMaxWidth(availableWidth);
      }
    };

    const resizeObserver = new ResizeObserver(handleResize);

    if (ellipsisTooltipRef.current) {
      resizeObserver.observe(ellipsisTooltipRef.current);
    }

    return () => {
      if (ellipsisTooltipRef.current) {
        resizeObserver.unobserve(ellipsisTooltipRef.current);
      }
    };
  }, [ellipsisTooltipRef.current]);

  return (
    <CellRow
      gridTemplateColumns={gridTemplateColumns}
      position={position}
      drawerHeight={localDrawerHeight}
      animationTime={animationTime}
      scrollPosition={scrollPosition}
      expanded={+localExpanded}
      onClick={
        onToggle
          ? () => {
              onToggle(index);
            }
          : onRowClick
            ? () => {
                onRowClick(index);
              }
            : undefined
      }
      scrollableCellMinWidth={scrollableCellMinWidth}
    >
      {headers.map((header, index) => {
        return (
          <Cell key={`cell-${index}`} isExpandable={header?.id === 'expandable'}>
            {renderCellValue(header)}
          </Cell>
        );
      })}
    </CellRow>
  );
};

export default TableRow;

const CellRow = styled.div<{
  gridTemplateColumns: string;
  position: keyof TableData['tables'];
  drawerHeight?: number;
  animationTime: string;
  scrollPosition: ScrollPosition;
  expanded: number;
  scrollableCellMinWidth: number;
}>`
  position: relative;
  display: grid;
  grid-template-columns: ${({ gridTemplateColumns }) => gridTemplateColumns};
  height: ${ROW_HEIGHT}px;
  cursor: pointer;
  transition: ${({ animationTime }) => `margin-bottom ${animationTime}ms ease`};
  margin-bottom: ${({ theme, drawerHeight }) => `calc(${drawerHeight}px + ${theme.spacing(1)})`};

  .cell {
    border-top: ${({ theme }) => `1px solid ${theme.palette.divider}`};
    border-bottom: ${({ theme }) => `1px solid ${theme.palette.divider}`};

    ${({ position, scrollableCellMinWidth }) =>
      position === 'scrollable' &&
      `
      min-width: ${scrollableCellMinWidth}px;
    `}
  }

  .cell:first-of-type {
    transition: border-radius 200ms ease-in-out;
    border-radius: ${({ theme, position, expanded }) => {
      if (position === 'left') {
        return expanded ? `${theme.shape.borderRadius}px 0 0 0` : `${theme.shape.borderRadius}px 0 0 ${theme.shape.borderRadius}px`;
      }
      return '0';
    }};
    border-left: ${({ theme, position }) => (position === 'left' ? `1px solid ${theme.palette.divider}` : 'none')};

    ${({ theme, position, scrollPosition, animationTime }) =>
      position === 'right' &&
      `
    &::before {
      content: '';
      position: absolute;
      top: 1px;
      left: -39px;
      height: calc(100% - 2px);
      width: 40px;
      z-index: 3;
      opacity: ${scrollPosition && scrollPosition !== 'end' ? 1 : 0};
      transition: opacity ${animationTime}ms ease;
      border-right: 1px solid ${theme.palette.divider};
      background: linear-gradient(to left, ${darken(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.2 : 0.1)}, transparent);
    }
    `}
  }

  .cell:last-of-type {
    transition: border-radius 200ms ease-in-out;
    border-right: ${({ theme, position }) => (position === 'right' ? `1px solid ${theme.palette.divider}` : 'none')};
    ${({ theme, position, expanded }) =>
      position === 'right' &&
      `
      border-radius: ${expanded ? `0 ${theme.shape.borderRadius}px 0px 0px` : `0 ${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0px`};
      overflow: hidden;
    `};

    ${({ theme, position, scrollPosition, animationTime }) =>
      position === 'left' &&
      `
    &::before {
      content: '';
      position: absolute;
      top: 1px;
      right: -39px;
      height: calc(100% - 2px);
      width: 40px;
      z-index: 3;
      opacity: ${scrollPosition && scrollPosition !== 'start' ? 1 : 0};
      transition: opacity ${animationTime}ms ease;
      border-left: 1px solid ${theme.palette.divider};
      background: linear-gradient(to right, ${darken(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.2 : 0.1)}, transparent);
    }
    `}
  }
`;

const IconWrapper = styled.div<{ expanded: number }>`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: auto;

  svg {
    transform: ${({ expanded }) => (expanded ? 'rotate(90deg)' : 'rotate(0deg)')};
    transition: transform 0.3s;
    transform-origin: center; /* Ensure rotation happens around the center */
  }
`;
